import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formSubmissionService } from "../services/api";
import { AuthContext } from "../context/AuthContext";

export const SubmissionDetailsPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado del flujo (secretaria)
  const [newStatus, setNewStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [updating, setUpdating] = useState(false);

  // Revision de referente
  const [correctionNote, setCorrectionNote] = useState("");
  const [reviewing, setReviewing] = useState(false);

  // Edicion de datos del servicio (secretaria)
  const [editingService, setEditingService] = useState(false);
  const [serviceData, setServiceData] = useState({});
  const [savingService, setSavingService] = useState(false);

  // Modal de preview de archivos
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLabel, setPreviewLabel] = useState("");
  const [previewIsImage, setPreviewIsImage] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isReferente = user?.role?.name === "referente";
  const isSecretaria = user?.role?.name === "secretaria";

  useEffect(() => {
    fetchSubmissionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const fetchSubmissionDetails = async () => {
    setLoading(true);
    try {
      const response =
        await formSubmissionService.getSubmissionById(submissionId);
      const s = response.data.submission;
      setSubmission(s);
      setNewStatus(s.status);
      setServiceData({
        entryDate: s.entryDate ? s.entryDate.split("T")[0] : "",
        period: s.period || "",
        cuit: s.cuit || "",
        locality: s.locality || "",
        province: s.province || "",
        concept: s.concept || "",
        serviceProvider: s.serviceProvider || "",
        invoiceNumber: s.invoiceNumber || "",
      });
    } catch (error) {
      console.error("Error fetching submission details:", error);
      alert("No se pudo cargar la solicitud");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      alert("Selecciona un estado");
      return;
    }
    if (newStatus === "denegado" && !rejectionReason.trim()) {
      alert("Debes proporcionar un motivo de denegacion");
      return;
    }
    setUpdating(true);
    try {
      await formSubmissionService.updateSubmissionStatus(
        submissionId,
        newStatus,
        rejectionReason,
      );
      alert("Estado actualizado exitosamente");
      fetchSubmissionDetails();
      setRejectionReason("");
    } catch (error) {
      alert("Error al actualizar el estado");
    } finally {
      setUpdating(false);
    }
  };

  const handleReferenteReview = async (action) => {
    if (action === "return" && !correctionNote.trim()) {
      alert(
        "Debes ingresar una nota de correccion para devolver el formulario.",
      );
      return;
    }
    setReviewing(true);
    try {
      await formSubmissionService.referenteReview(
        submissionId,
        action,
        correctionNote,
      );
      alert(
        action === "confirm"
          ? "Solicitud confirmada"
          : "Formulario devuelto para correccion",
      );
      fetchSubmissionDetails();
      setCorrectionNote("");
    } catch (err) {
      alert("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setReviewing(false);
    }
  };

  const handleSaveServiceData = async () => {
    setSavingService(true);
    try {
      await formSubmissionService.updateServiceData(submissionId, serviceData);
      alert("Datos del servicio actualizados");
      setEditingService(false);
      fetchSubmissionDetails();
    } catch (err) {
      alert(
        "Error al guardar: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setSavingService(false);
    }
  };

  // Construye la URL base del archivo SIN token (el token va solo en header)
  const buildFileUrl = (filename) => {
    const API_BASE =
      process.env.REACT_APP_API_URL || "http://localhost:5000/api";
    const BASE_URL = API_BASE.replace("/api", "");
    return `${BASE_URL}${filename}`;
  };

  // Carga el archivo via fetch con Authorization header y retorna un blob URL
  const fetchFileAsBlob = async (filename) => {
    const url = buildFileUrl(filename);
    const storedToken = localStorage.getItem("token");
    const res = await fetch(url, {
      headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {},
    });
    if (!res.ok) throw new Error("Error al obtener el archivo");
    return { blob: await res.blob(), mimeType: res.headers.get("content-type") || "" };
  };

  const handleDownloadFile = async (filename, label) => {
    try {
      const { blob } = await fetchFileAsBlob(filename);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const parts = filename.split("/");
      a.download = parts[parts.length - 1] || label;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      alert("No se pudo descargar el archivo");
    }
  };

  const handlePreviewFile = async (filename, label) => {
    setPreviewLoading(true);
    try {
      const { blob, mimeType } = await fetchFileAsBlob(filename);
      const blobUrl = URL.createObjectURL(blob);
      // Revocar blob URL anterior si existía
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(blobUrl);
      setPreviewLabel(label);
      setPreviewIsImage(mimeType.startsWith("image/"));
    } catch {
      alert("No se pudo cargar la vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewLabel("");
  };

  const FileBlock = ({ filename, label }) => {
    if (!filename) return <span className="text-slate-400 text-sm">—</span>;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={previewLoading}
          onClick={() => handlePreviewFile(filename, label)}
          className="text-xs font-semibold text-primary-700 border border-primary-300 rounded px-3 py-1 hover:bg-primary-50 disabled:opacity-50"
        >
          Ver
        </button>
        <button
          type="button"
          onClick={() => handleDownloadFile(filename, label)}
          className="text-xs font-semibold text-green-700 border border-green-300 rounded px-3 py-1 hover:bg-green-50"
        >
          Descargar
        </button>
        <span className="text-xs text-slate-500 truncate">{label}</span>
      </div>
    );
  };

  const statusColor = {
    en_revision: "bg-amber-100 text-amber-700",
    aprobado: "bg-green-100 text-green-700",
    denegado: "bg-red-100 text-red-700",
  };
  const workflowColor = {
    pendiente_solicitante: "bg-blue-100 text-blue-700",
    pendiente_revision: "bg-amber-100 text-amber-700",
    necesita_correccion: "bg-red-100 text-red-700",
    confirmado: "bg-green-100 text-green-700",
  };
  const workflowLabel = {
    pendiente_solicitante: "Esperando solicitante",
    pendiente_revision: "Pendiente de revisión",
    necesita_correccion: "Necesita corrección",
    confirmado: "Confirmado por referente",
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>
    );

  if (!submission)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-slate-500">Solicitud no encontrada.</p>
      </div>
    );

  return (
    <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Modal de preview */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex flex-col items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-xl p-4 max-w-[90vw] max-h-[90vh] flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <strong className="text-sm text-slate-800">{previewLabel}</strong>
              <button onClick={closePreview} className="text-slate-500 hover:text-slate-800 text-xl leading-none ml-4">✕</button>
            </div>
            {previewIsImage ? (
              <img src={previewUrl} alt={previewLabel} className="max-w-[80vw] max-h-[75vh] object-contain" />
            ) : (
              <iframe src={previewUrl} title={previewLabel} className="w-[80vw] h-[75vh] border-0" />
            )}
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-center text-primary-700 underline">
              Abrir en nueva pestaña
            </a>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => navigate("/dashboard")} className="btn-secondary py-1.5 px-3 text-sm">
          ← Volver
        </button>
        <h1 className="text-2xl font-bold text-slate-800 flex-1">Detalles de la Solicitud</h1>
        <div className="flex gap-2 flex-wrap">
          <span className={`badge ${statusColor[submission.status] || "bg-slate-100 text-slate-600"}`}>
            {submission.status?.toUpperCase()}
          </span>
          {submission.workflowStatus && (
            <span className={`badge ${workflowColor[submission.workflowStatus] || "bg-slate-100 text-slate-600"}`}>
              {workflowLabel[submission.workflowStatus] || submission.workflowStatus}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {/* Datos del Fallecido */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-4">Datos del Fallecido</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><p className="label">DNI</p><p className="text-slate-800">{submission.dniDeceased || "—"}</p></div>
            <div><p className="label">Nombres y Apellidos</p><p className="text-slate-800">{submission.namingDeceased || "—"}</p></div>
            <div className="sm:col-span-2"><p className="label">Domicilio</p><p className="text-slate-800">{submission.domicileDeceased || "—"}</p></div>
            <div><p className="label">CUIT</p><p className="text-slate-800">{submission.cuit || "—"}</p></div>
            <div><p className="label">Localidad</p><p className="text-slate-800">{submission.locality || "—"}</p></div>
            <div><p className="label">Provincia</p><p className="text-slate-800">{submission.province || "—"}</p></div>
          </div>
        </div>

        {/* Datos del Solicitante */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-4">Datos del Solicitante</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><p className="label">Nombre</p><p className="text-slate-800">{submission.requestorName || "—"}</p></div>
            <div><p className="label">DNI del Solicitante</p><p className="text-slate-800">{submission.requestorDni || "—"}</p></div>
          </div>
        </div>

        {/* Datos del Servicio */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700">Datos del Servicio</h2>
            {isSecretaria && !editingService && (
              <button onClick={() => setEditingService(true)} className="btn-primary py-1 px-3 text-xs">Editar</button>
            )}
          </div>
          {editingService ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="label">Fecha de Entrada</label><input type="date" value={serviceData.entryDate} onChange={(e) => setServiceData(p => ({ ...p, entryDate: e.target.value }))} className="input" /></div>
                <div><label className="label">Período</label><input type="text" value={serviceData.period} onChange={(e) => setServiceData(p => ({ ...p, period: e.target.value }))} className="input" /></div>
                <div><label className="label">CUIT</label><input type="text" value={serviceData.cuit} onChange={(e) => setServiceData(p => ({ ...p, cuit: e.target.value }))} className="input" /></div>
                <div><label className="label">Localidad</label><input type="text" value={serviceData.locality} onChange={(e) => setServiceData(p => ({ ...p, locality: e.target.value }))} className="input" /></div>
                <div><label className="label">Provincia</label><input type="text" value={serviceData.province} onChange={(e) => setServiceData(p => ({ ...p, province: e.target.value }))} className="input" /></div>
                <div><label className="label">Concepto</label><input type="text" value={serviceData.concept} onChange={(e) => setServiceData(p => ({ ...p, concept: e.target.value }))} className="input" /></div>
                <div><label className="label">Empresa Prestadora</label><input type="text" value={serviceData.serviceProvider} onChange={(e) => setServiceData(p => ({ ...p, serviceProvider: e.target.value }))} className="input" /></div>
                <div><label className="label">Nro de Factura</label><input type="text" value={serviceData.invoiceNumber} onChange={(e) => setServiceData(p => ({ ...p, invoiceNumber: e.target.value }))} className="input" /></div>
              </div>
              <div className="flex gap-3">
                <button onClick={handleSaveServiceData} disabled={savingService} className="btn-primary">{savingService ? "Guardando..." : "Guardar cambios"}</button>
                <button onClick={() => setEditingService(false)} className="btn-secondary">Cancelar</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><p className="label">Fecha de Entrada</p><p className="text-slate-800">{submission.entryDate ? new Date(submission.entryDate).toLocaleDateString() : "—"}</p></div>
              <div><p className="label">Período</p><p className="text-slate-800">{submission.period || "—"}</p></div>
              <div className="sm:col-span-2"><p className="label">Concepto</p><p className="text-slate-800">{submission.concept || "—"}</p></div>
              <div><p className="label">Empresa Prestadora</p><p className="text-slate-800">{submission.serviceProvider || "—"}</p></div>
              <div><p className="label">Nro de Factura</p><p className="text-slate-800">{submission.invoiceNumber || "—"}</p></div>
              <div className="sm:col-span-2"><p className="label">Nota</p><p className="text-slate-800">{submission.notes || "—"}</p></div>
            </div>
          )}
        </div>

        {/* Archivos Adjuntos */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-4">Archivos Adjuntos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: "attachmentSolicitude1", label: "Solicitud (doc empresa firmada)" },
              { key: "attachmentSolicitude2", label: "Solicitud 2 (datos del doc)" },
              { key: "attachmentDeathCertificate1", label: "Cert. Defunción (Sanatorio)" },
              { key: "attachmentDeathCertificate2", label: "Cert. Defunción (Registro)" },
              { key: "attachmentDniDeceased", label: "DNI del Fallecido" },
              { key: "attachmentDniRequestor", label: "DNI del Solicitante" },
            ].map(({ key, label }) => (
              <div key={key} className="rounded-lg border border-slate-200 p-3">
                <p className="label mb-2">{label}</p>
                <FileBlock filename={submission[key]} label={label} />
              </div>
            ))}
          </div>
        </div>

        {/* Revisión de Referente */}
        {isReferente && submission.workflowStatus === "pendiente_revision" && (
          <div className="card p-6 border-l-4 border-amber-400">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-700 mb-3">Revisión de Referente</h2>
            <p className="text-sm text-slate-600 mb-4">
              Revisá los datos y documentos cargados por el solicitante. Podés confirmar la solicitud o devolverla para corrección.
            </p>
            <div className="mb-4">
              <label className="label">Nota de corrección (requerida para devolver)</label>
              <textarea
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                placeholder="Indicá qué debe corregir el solicitante..."
                rows={3}
                className="input"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleReferenteReview("confirm")} disabled={reviewing} className="btn-primary">
                {reviewing ? "..." : "Confirmar solicitud"}
              </button>
              <button onClick={() => handleReferenteReview("return")} disabled={reviewing} className="btn-danger">
                {reviewing ? "..." : "Devolver para corrección"}
              </button>
            </div>
          </div>
        )}

        {isReferente && submission.workflowStatus === "confirmado" && (
          <div className="card p-5 border-l-4 border-green-500">
            <p className="text-sm font-semibold text-green-700">Esta solicitud ya fue confirmada por el referente.</p>
          </div>
        )}

        {submission.correctionNote && (
          <div className="card p-5 border-l-4 border-red-400">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-1">Nota de corrección</p>
            <p className="text-sm text-slate-700">{submission.correctionNote}</p>
          </div>
        )}

        {/* Cambiar Estado — Secretaria */}
        {isSecretaria && (
          <div className="card p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-4">Cambiar Estado de la Solicitud</h2>
            <div className="space-y-4 max-w-sm">
              <div>
                <label htmlFor="statusSelect" className="label">Nuevo Estado</label>
                <select id="statusSelect" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="input">
                  <option value="en_revision">En Revisión</option>
                  <option value="aprobado">Aprobado</option>
                  <option value="denegado">Denegado</option>
                </select>
              </div>
              {newStatus === "denegado" && (
                <div>
                  <label htmlFor="rejectionReason" className="label">Motivo de Denegación *</label>
                  <textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explicá el motivo de la denegación..."
                    rows={4}
                    required
                    className="input"
                  />
                </div>
              )}
              <button onClick={handleStatusUpdate} disabled={updating || newStatus === submission.status} className="btn-primary">
                {updating ? "Actualizando..." : "Guardar Cambios"}
              </button>
            </div>
          </div>
        )}

        {/* Auditoría */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-4">Información de Auditoría</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><p className="label">Fecha de Carga</p><p className="text-slate-800">{new Date(submission.submittedAt || submission.createdAt).toLocaleString()}</p></div>
            <div><p className="label">Cargado por</p><p className="text-slate-800">{submission.submittedBy?.email || "—"}</p></div>
            {submission.reviewedAt && (
              <>
                <div><p className="label">Fecha de Revisión</p><p className="text-slate-800">{new Date(submission.reviewedAt).toLocaleString()}</p></div>
                <div><p className="label">Revisado por</p><p className="text-slate-800">{submission.reviewedBy?.email || "—"}</p></div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
